import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { HTTP_STATUS, ROUTES } from '@/app/config/constants';
import { getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { logger } from '@/core/logging/logger';
import { clearStoredTheme } from '@/shared/hooks/useTheme';
import { tokenService } from '@/shared/services/auth/tokenService';
import { isPasswordChangeRequiredResponse } from '@/shared/services/auth/passwordChange';
import { isEntityInactiveResponse } from './entityInactive';
import { appQueryClient } from '@/shared/query';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Marks a request that has already been retried after a token refresh, so
     * a second 401 cannot start an infinite refresh loop. Axios does not
     * serialize unknown config keys, so this never reaches the backend.
     */
    _retry?: boolean;
    /**
     * Opts a request out of the global 403 -> /unauthorized navigation, so its
     * caller can report the refusal in place instead.
     *
     * For a request the user did not initiate -- a menu, a lookup list, a
     * table load -- throwing the whole app out of the shell is the wrong
     * response to "you may not read this one thing". Set it on background
     * reads; leave it off wherever a 403 really does mean the page is barred.
     */
    _allowForbidden?: boolean;
    /**
     * Marks a request already replayed once with a freshly minted token after a 403, so a refusal that
     * is genuine surfaces on the second pass instead of looping.
     */
    _retriedForbidden?: boolean;
    /**
     * Sends the request WITHOUT the shell's selected entity (`X-Entity-Id`), so the platform answers for every
     * entity the caller is assigned to -- e.g. the entities User Management may grant as Additional Entity Admin
     * Access. Never wider than the caller's own assignments: the scope is still the server's.
     */
    _allEntities?: boolean;
    /** How many times this request has been retried after a transient failure -- see `isTransientFailure`. */
    _transientRetries?: number;
    /** Opts a read out of the transient-failure retry, e.g. a poll that will simply run again. */
    _noRetry?: boolean;
  }
}

/**
 * The header carrying one id per logical request, matching `PlatformConstants.CORRELATION_ID_HEADER` on the
 * platform, whose `CorrelationIdFilter` puts it in every log line it writes for that request and echoes it on the
 * response. A replay -- after a token refresh or a transient failure -- keeps the id it was first sent with, so one
 * user action reads as one id in the platform's logs.
 */
export const CORRELATION_HEADER = 'X-Correlation-Id';

/** A new correlation id: a UUID where the browser offers one, else a random id of the same length. */
export function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

/** At most this many retries of a transient failure, after the first attempt. */
export const MAX_TRANSIENT_RETRIES = 2;

/** Backoff before retry n (1-based): 300ms, then 600ms. */
export const transientRetryDelay = (attempt: number): number => 300 * 2 ** (attempt - 1);

const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Gateway and availability failures, the ones that tend to pass on their own. */
const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/**
 * Whether a failed request may be sent again without the user asking.
 *
 * Only reads (GET, HEAD, OPTIONS): a POST, PUT, PATCH or DELETE that timed out may well have been applied, and
 * sending it again could create a second record or repeat an action -- so a write's failure always goes back to
 * the caller, whatever it was. A read is retried only when the failure says nothing about the request itself: no
 * response at all (network drop, timeout) or a 502/503/504 from the gateway. Never a 4xx, never a cancelled
 * request, never the auth endpoints.
 */
export function isTransientFailure(error: AxiosError): boolean {
  const config = error.config;
  if (!config || config._noRetry === true) return false;
  if (error.code === 'ERR_CANCELED') return false;
  if (!IDEMPOTENT_METHODS.has((config.method ?? 'get').toUpperCase())) return false;
  if (isExcluded(config.url ?? '')) return false;
  if ((config._transientRetries ?? 0) >= MAX_TRANSIENT_RETRIES) return false;
  const status = error.response?.status;
  return status === undefined || TRANSIENT_STATUSES.has(status);
}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const REFRESH_PATH = '/auth/refresh';

/**
 * The header naming the legal entity every request is scoped to.
 *
 * Matches `AssignedEntityScope.SELECTED_ENTITY_HEADER` on the platform, which honours it inside
 * `assignedEntityIds()` — so it narrows a caller to one of their assignments and can never widen beyond
 * them. Naming an entity the caller does not hold is refused there, not ignored.
 */
export const ENTITY_HEADER = 'X-Entity-Id';

/**
 * Endpoints that run before the user has a session. Sending a stale (often
 * expired) bearer token to these confuses the server -- notably /auth/refresh,
 * which must be authenticated by the refresh token in its body, not a header.
 */
const AUTH_EXCLUDED_PATHS: readonly string[] = [
  '/auth/login',
  REFRESH_PATH,
  // The platform's one-time-code routes. `/auth/otp/` covers both `request`
  // and `verify`, matching its own `platform.security.public-paths` entry
  // `/api/v1/auth/otp/**`.
  '/auth/otp/',
  '/auth/password/forgot',
  '/auth/password/reset',
  '/auth/password/forgot-link',
  '/auth/password/reset-link',
  '/auth/password/verify-otp',
  '/auth/password/verify-link',
];

/**
 * Everything the interceptors need from the outside world.
 *
 * Injected rather than imported: `shared/` must not reach into `features/`,
 * and importing the store here would create a cycle
 * (store -> rootSaga -> authSaga -> authService -> apiClient). app/ owns the
 * wiring; see main.tsx.
 */
export interface InterceptorDeps {
  /** Current access token, normally `store.getState().auth.token`. */
  getToken: () => string | null;
  /** Scheme for the Authorization header, e.g. "Bearer". */
  getTokenType?: () => string;
  /**
   * The legal entity the user is working in, sent as `X-Entity-Id` on every request.
   *
   * The platform narrows `AssignedEntityScope` to it — the one place its hierarchy queries already scope
   * by — so the whole application follows the picker without any endpoint taking a parameter. `null`
   * before the profile has been read, and for a caller with no assignments; no header is sent then, and
   * the caller sees everything they are assigned to, which for that caller is everything.
   */
  getSelectedEntityId?: () => number | null;
  /** A refresh succeeded -- push the rotated tokens into app state. */
  onTokensRefreshed: (tokens: {
    token: string;
    refreshToken?: string;
    tokenType?: string;
    expiresAt?: number;
  }) => void;
  /** Refresh is impossible or failed -- tear the session down. */
  onSessionExpired: () => void;
}

/**
 * POST /auth/refresh answers with the standard envelope
 * (mdfiles/loginapi.md section 2), so the tokens sit under `data`.
 */
interface RefreshEnvelope {
  success?: boolean;
  message?: string;
  data?: {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
    tokenType?: string;
  };
}

interface QueuedRequest {
  resolve: (token: string) => void;
  reject: (reason: unknown) => void;
}

function isExcluded(url: string): boolean {
  return AUTH_EXCLUDED_PATHS.some((path) => url.includes(path));
}

/**
 * Resources that belong to no legal entity: Country, State and City are global reference data, one list shared
 * by every entity (platform `V20260930100000`, openspec change `geography-is-global`). Their master screens and
 * every Country / State / City dropdown call these, and they go without `X-Entity-Id`: the platform ignores it
 * for them, and sending it would dress a global list up as one entity's.
 *
 * Matched on the first path segment (after an optional `/api/v1`), so `/countries/7` and `/states?countryId=7`
 * are covered and a longer path merely containing the word is not.
 */
const ENTITY_AGNOSTIC_RESOURCE = /^(?:https?:\/\/[^/]+)?(?:\/api\/v1)?\/(?:countries|states|cities)(?:[/?#]|$)/;

/** Whether a request addresses a global resource that must not carry the selected entity. */
export function isEntityAgnostic(url: string): boolean {
  return ENTITY_AGNOSTIC_RESOURCE.test(url);
}

/** POST, PUT, PATCH or DELETE -- the verbs that can change what the bell counts. */
function isWrite(method: string | undefined): boolean {
  if (method === undefined) return false;
  const verb = method.toUpperCase();
  return verb === 'POST' || verb === 'PUT' || verb === 'PATCH' || verb === 'DELETE';
}

export function setupInterceptors(client: AxiosInstance, deps: InterceptorDeps): () => void {
  let isRefreshing = false;
  let queue: QueuedRequest[] = [];

  const flushQueue = (token: string): void => {
    const pending = queue;
    queue = [];
    pending.forEach((entry) => {
      entry.resolve(token);
    });
  };

  const failQueue = (reason: unknown): void => {
    const pending = queue;
    queue = [];
    pending.forEach((entry) => {
      entry.reject(reason);
    });
  };

  const scheme = (): string => deps.getTokenType?.() ?? tokenService.getTokenType();

  const replayWith = (
    config: InternalAxiosRequestConfig,
    token: string,
  ): Promise<AxiosResponse> => {
    config.headers.Authorization = `${scheme()} ${token}`;
    return client.request(config);
  };

  const retryWithToken = (
    config: InternalAxiosRequestConfig,
    token: string,
  ): Promise<AxiosResponse> => {
    config._retry = true;
    return replayWith(config, token);
  };

  /**
   * Mints a new access token and persists it, or returns null if that is not possible.
   *
   * Shared by the 401 path and the 403 path. Both need the same thing — a token rebuilt from the grant
   * tables — and the platform rebuilds every claim on a refresh exactly as it does on a sign-in.
   */
  const mintToken = async (refreshToken: string): Promise<string | null> => {
    const { data: body } = await client.post<RefreshEnvelope>(REFRESH_PATH, { refreshToken });
    const tokens = body?.data;
    if (!tokens?.accessToken) return null;

    tokenService.setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      tokenType: tokens.tokenType,
      expiresIn: tokens.expiresIn,
    });
    deps.onTokensRefreshed({
      token: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      tokenType: tokens.tokenType,
      ...(typeof tokens.expiresIn === 'number'
        ? { expiresAt: Date.now() + tokens.expiresIn * 1000 }
        : {}),
    });
    return tokens.accessToken;
  };

  /**
   * Section 6: a failed refresh clears every stored key and sends the user to
   * the login page. `onSessionExpired` does the navigating so this stays out
   * of app-layer concerns.
   */
  const endSession = (reason: unknown): Promise<never> => {
    tokenService.clearAll();
    /*
      The organization theme is session-scoped, like every other stored key:
      sign-out drops it so the next sign-in re-reads the organization's own
      theme instead of inheriting a stale one.
    */
    clearStoredTheme();
    deps.onSessionExpired();
    return Promise.reject(normalizeError(reason));
  };

  const requestId = client.interceptors.request.use((config) => {
    config.headers.Accept ??= 'application/json';

    /* One id per logical request; a replay arrives with the header already set and keeps it. */
    if (!config.headers.get(CORRELATION_HEADER)) {
      config.headers.set(CORRELATION_HEADER, newCorrelationId());
    }

    // An Authorization header already on the config is left alone. Replayed
    // requests carry the freshly refreshed token, and re-reading it from app
    // state here would overwrite it with the stale one if the store has not
    // applied onTokensRefreshed yet.
    if (!isExcluded(config.url ?? '') && !config.headers.Authorization) {
      // Falls back to storage so a request fired before the store rehydrates
      // still carries a token.
      const token = deps.getToken() ?? tokenService.getAccessToken();
      if (token) {
        config.headers.Authorization = `${scheme()} ${token}`;
      }
    }

    /*
      The entity scope, on every request the session makes.

      Not set on the auth endpoints: sign-in and refresh happen before there is a selection to send, and
      neither reads anything the scope narrows. A caller with no selection sends no header at all, which
      the platform reads as "everything I am assigned to".
    */
    /*
      Through `AxiosHeaders.set`, not a bracket assignment. Axios defines accessors for the well-known
      names -- which is why `Authorization` above works that way -- and keeps everything else in an
      internal, case-normalised store. A bracket write lands as a plain own property that is dropped when
      axios flattens the headers on the way to the adapter, so the header silently never left.
    */
    if (
      !isExcluded(config.url ?? '') &&
      !isEntityAgnostic(config.url ?? '') &&
      config._allEntities !== true &&
      config.headers.get(ENTITY_HEADER) === undefined
    ) {
      const entityId = deps.getSelectedEntityId?.() ?? null;
      if (entityId !== null) {
        config.headers.set(ENTITY_HEADER, String(entityId));
      }
    }
    return config;
  });

  const responseId = client.interceptors.response.use(
    (response) => {
      /*
        Every successful write re-checks the bell.

        Here rather than on the mutation cache, because most of this application does not write through
        TanStack Query: the BIDs feature calls `rfqService.hold(id)` directly, Organization Setup goes
        through a saga, and the masters have their own engine. A hook on `useServerMutation` therefore
        misses nearly every action that can raise a notification -- which is exactly what it did: publishing
        a BID left the badge unchanged until the page was refreshed.

        The transport is the one place all three styles meet. A write is the only thing that can produce a
        notification, so this is the complete set of moments the count can change from this tab, whichever
        way the screen was built.
      */
      if (isWrite(response.config.method)) {
        void appQueryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
      return response;
    },
    async (error: unknown) => {
      const axiosError = error as AxiosError;
      const config = axiosError.config;
      const status = axiosError.response?.status;

      /* A read that failed for a reason outside the request itself is tried again, briefly, before anyone sees it. */
      if (config && isTransientFailure(axiosError)) {
        config._transientRetries = (config._transientRetries ?? 0) + 1;
        logger.warn('Transient failure; retrying the read', {
          url: config.url,
          status,
          attempt: config._transientRetries,
        });
        await wait(transientRetryDelay(config._transientRetries));
        return client.request(config);
      }

      /*
        A hard navigation, so it discards app state -- which is right when the
        user has walked into a page they may not see, and wrong when some
        background read was refused. `_allowForbidden` lets the caller say
        which of the two this is.
      */
      if (status === HTTP_STATUS.FORBIDDEN) {
        /*
          A 403 can mean the token is out of date rather than that the user may not pass.

          The platform authorizes from the grants baked into the access token at sign-in; the client
          decides what to OFFER from `/users/me`, which is read fresh on every sign-in AND on every page
          refresh. So when an administrator changes a role, a reload updates the button while the token
          keeps the old claims for the rest of its fifteen minutes — the screen shows Add, and the POST
          behind it is refused. Nothing recovered on its own, because the app re-mints on a 401 and this
          is a 403.

          So: mint once, replay once. If the second attempt is refused too, the refusal is real and
          falls through below. One extra round trip on a genuine refusal, bounded by `_retriedForbidden`
          — this cannot loop, and it never turns a "no" into a "yes": the platform decides both times.
        */
        /*
          A refusal from the auth endpoints themselves is never a page the user walked into, so it is
          neither replayed nor navigated: re-minting would recurse, and sending someone to /unauthorized
          because their token could not be renewed reports the wrong thing entirely. The 401 path owns
          what a dead session means.
        */
        if (config && isExcluded(config.url ?? '')) {
          return Promise.reject(normalizeError(error));
        }

        /*
          The selected entity was made Inactive mid-session (2026-10-10). Neither a stale token nor a page the
          user may not see: a fresh token is refused the same way, and the Unauthorized page would hide the
          one thing to say -- "Please contact your administrator." -- which the screen reports as it stands.
        */
        if (isEntityInactiveResponse(axiosError.response?.data)) {
          return Promise.reject(normalizeError(error));
        }

        /*
          The user still holds their first password (2026-10-15), and this was a write. Not a stale token -- a
          fresh one says the same until the password is changed -- and not a page they may not see, so it is
          neither replayed nor sent to /unauthorized. The Change Password dialog is already open over the page;
          the refusal is simply passed back to the caller.
        */
        if (isPasswordChangeRequiredResponse(axiosError.response?.data)) {
          return Promise.reject(normalizeError(error));
        }

        const refreshToken = tokenService.getRefreshToken();
        const replayable = config !== undefined && config._retriedForbidden !== true && refreshToken !== null;

        if (replayable && config && refreshToken) {
          config._retriedForbidden = true;
          try {
            const minted = isRefreshing
              ? await new Promise<string>((resolve, reject) => {
                  queue.push({ resolve, reject });
                })
              : await (async () => {
                  isRefreshing = true;
                  try {
                    const token = await mintToken(refreshToken);
                    if (token) flushQueue(token);
                    return token;
                  } finally {
                    isRefreshing = false;
                  }
                })();

            if (minted) return await replayWith(config, minted);
          } catch (refreshFailure: unknown) {
            /*
              The refusal stands, and the session is NOT ended: a 403 says nothing about whether the
              token is still valid, so tearing the session down here would sign out a user who simply
              opened a page they may not see.
            */
            logger.warn('Could not re-mint the session after a refusal; reporting it as refused', {
              reason: getErrorMessage(normalizeError(refreshFailure)),
            });
          }
        }

        /*
          A refused WRITE is reported where it was made, not by leaving the page (2026-10-16).

          The Unauthorized page is for a page the user walked into and may not see. A refused save -- creating a BID
          or Supplier Onboarding template, say -- is an action on a page they may see, and the platform's message is
          the one thing that says why: a missing grant, an entity they are not assigned to, an Inactive one. Sending
          them to /unauthorized discarded the form and showed "unauthorized" in its place. So a refused POST, PUT,
          PATCH or DELETE comes back to the caller, whose save path already shows the platform's message; a refused
          read still navigates, unless it opted out with `_allowForbidden`. The method, path and the platform's code
          are logged, so the refusal can be traced from the console as well.
        */
        const method = (config?.method ?? 'get').toUpperCase();
        const isWrite = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
        const refused = normalizeError(error);
        logger.warn('Request refused (403)', {
          method,
          url: config?.url,
          code: (axiosError.response?.data as { code?: unknown } | undefined)?.code,
          message: refused.message,
        });
        if (!isWrite && !config?._allowForbidden) {
          window.location.assign(ROUTES.UNAUTHORIZED);
        }
        return Promise.reject(refused);
      }

      if (status !== HTTP_STATUS.UNAUTHORIZED || !config) {
        return Promise.reject(normalizeError(error));
      }

      // A 401 from the refresh endpoint itself means the refresh token is dead.
      // Never queue this one -- there is nothing left to wait for.
      if (isExcluded(config.url ?? '') && (config.url ?? '').includes(REFRESH_PATH)) {
        logger.warn('Refresh token rejected; ending session');
        failQueue(error);
        return endSession(error);
      }

      /*
        A 401 from any other public auth endpoint -- sign-in, the one-time code, password recovery -- is a
        refused credential ("Invalid OTP. Please enter a valid OTP."), not an expired session: none of them
        was sent a token to expire. So it is neither refreshed nor turned into a sign-out, which used to reset
        the sign-in state (the OTP screen's email among it) and send the next attempt with an empty address.
        The refusal is simply passed back to the caller, which reports the server's own message.
      */
      if (isExcluded(config.url ?? '')) {
        return Promise.reject(normalizeError(error));
      }

      // Already retried once with a fresh token and still 401 -- stop.
      if (config._retry) {
        return endSession(error);
      }

      // A 401 with no refresh token to spend is the end of the session, not a pause in it.
      // This used to notify the app without clearing storage, which left an expired access
      // token behind for the next reload to hydrate from and 401 on all over again.
      const refreshToken = tokenService.getRefreshToken();
      if (!refreshToken) {
        logger.warn('Session rejected and no refresh token to renew it; ending session');
        return endSession(error);
      }

      // A refresh is already in flight: park this request and replay it with
      // whatever token that refresh produces. One refresh, many retries.
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          queue.push({
            resolve: (token) => {
              retryWithToken(config, token).then(resolve, reject);
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        // A 200 that carries no tokens is as dead as a 401. `mintToken` persists and publishes the
        // rotated pair; the API rotates the refresh token too, so both are re-persisted there.
        const minted = await mintToken(refreshToken);
        if (!minted) {
          logger.warn('Refresh returned no access token; ending session');
          failQueue(error);
          return await endSession(error);
        }

        flushQueue(minted);
        return await retryWithToken(config, minted);
      } catch (refreshError) {
        failQueue(refreshError);
        return await endSession(refreshError);
      } finally {
        isRefreshing = false;
      }
    },
  );

  return () => {
    client.interceptors.request.eject(requestId);
    client.interceptors.response.eject(responseId);
  };
}

export { AUTH_EXCLUDED_PATHS };
